import { useEffect, useState, type ChangeEvent, type ReactNode } from 'react';
import { chileanPesoCaret, digitsBeforeChileanPesoCaret, formatChileanPeso } from '@/lib/format-chilean-peso';
import { fieldTypeLabel } from '@/lib/user-custom-field-types';

const PREVIEW_INPUT =
  'w-full rounded-lg border border-dash-outline-variant bg-white px-4 py-3 text-base text-dash-on-surface outline-none transition-all focus:border-dash-secondary focus:ring-2 focus:ring-dash-secondary/20';

interface PreviewField {
  id: string;
  name: string;
  slug: string;
  type: string;
  placeholder?: string | null;
  helpText?: string | null;
  required?: boolean;
  options?: Array<{ label: string; value: string; isActive?: boolean }>;
  maxFiles?: number;
  optionsLayout?: 'vertical' | 'horizontal';
}

export function CustomFieldPreviewField({ field, className }: { field: PreviewField; className?: string }) {
  const root = `space-y-2 ${className ?? ''}`;
  const inputId = `preview-cf-${field.id}`;
  const label = (
    <label htmlFor={inputId} className="text-sm font-medium text-dash-on-surface-variant">
      {field.name}
      {field.required ? <span className="text-error-600"> *</span> : null}
    </label>
  );
  const hint = field.helpText ? <p className="text-xs text-dash-on-surface-variant">{field.helpText}</p> : null;
  const placeholder = field.placeholder ?? '';
  const options = (field.options ?? []).filter((option) => option.isActive !== false && (option.label || option.value));

  switch (field.type) {
    case 'textarea':
      return (
        <div className={root}>
          {label}
          <textarea
            id={inputId}
            name={inputId}
            rows={3}
            required={field.required}
            placeholder={placeholder}
            className={`${PREVIEW_INPUT} resize-none`}
          />
          {hint}
        </div>
      );

    case 'select':
      return (
        <div className={root}>
          {label}
          <div className="relative">
            <select id={inputId} name={inputId} required={field.required} defaultValue="" className={`${PREVIEW_INPUT} appearance-none pr-10`}>
              <option value="">Seleccionar...</option>
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label || option.value}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-dash-on-surface-variant" aria-hidden="true">
              <span className="material-symbols-outlined">expand_more</span>
            </span>
          </div>
          {hint}
        </div>
      );

    case 'multiselect':
    case 'radio': {
      const sideBySide = field.type === 'multiselect' && field.optionsLayout === 'horizontal';
      return (
        <div className={root}>
          <span className="text-sm font-medium text-dash-on-surface-variant">
            {field.name}
            {field.required ? <span className="text-error-600"> *</span> : null}
          </span>
          <div className={`${sideBySide ? 'flex flex-wrap gap-x-6 gap-y-3' : 'space-y-2'} rounded-lg border border-dash-outline-variant bg-white p-3`}>
            {options.length === 0 ? (
              <p className="text-sm text-dash-on-surface-variant">Sin opciones configuradas.</p>
            ) : (
              options.map((option) => (
                <label key={option.value} className={`${sideBySide ? 'inline-flex whitespace-nowrap' : 'flex'} items-center gap-2 text-sm text-dash-on-surface`}>
                  <input
                    type={field.type === 'radio' ? 'radio' : 'checkbox'}
                    name={field.type === 'radio' ? inputId : `${inputId}-${option.value}`}
                    value={option.value}
                    required={field.required && field.type === 'radio'}
                    className="border-dash-outline-variant"
                  />
                  {option.label || option.value}
                </label>
              ))
            )}
          </div>
          {hint}
        </div>
      );
    }

    case 'checkbox':
      return (
        <div className={root}>
          <label className="flex items-center gap-3 text-sm text-dash-on-surface">
            <input
              id={inputId}
              name={inputId}
              type="checkbox"
              required={field.required}
              className="h-4 w-4 rounded border-dash-outline-variant"
            />
            {field.name}
          </label>
          {hint}
        </div>
      );

    case 'switch':
      return (
        <div className={root}>
          <label className="flex items-center gap-3 text-sm text-dash-on-surface">
            <span className="relative inline-block h-6 w-11 shrink-0">
              <input id={inputId} name={inputId} type="checkbox" className="peer sr-only" />
              <span className="block h-6 w-11 rounded-full bg-secondary-200 peer-checked:bg-dash-primary" aria-hidden="true" />
              <span
                className="pointer-events-none absolute left-0.5 top-0.5 block h-5 w-5 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5"
                aria-hidden="true"
              />
            </span>
            {field.name}
          </label>
          {hint}
        </div>
      );

    case 'file':
    case 'image':
      return <PreviewFileField field={field} root={root} label={label} hint={hint} inputId={inputId} />;

    case 'color':
      return (
        <div className={root}>
          {label}
          <input id={inputId} name={inputId} type="color" className="h-11 w-full rounded-lg border border-dash-outline-variant" />
          {hint}
        </div>
      );

    case 'peso':
      return <PreviewPesoField field={field} root={root} label={label} hint={hint} inputId={inputId} placeholder={placeholder} />;

    default: {
      const inputType =
        field.type === 'number' || field.type === 'decimal'
          ? 'number'
          : field.type === 'email'
            ? 'email'
            : field.type === 'url'
              ? 'url'
              : field.type === 'phone'
                ? 'tel'
                : field.type === 'date'
                  ? 'date'
                  : field.type === 'datetime'
                    ? 'datetime-local'
                    : 'text';

      return (
        <div className={root}>
          {label}
          <input
            id={inputId}
            name={inputId}
            type={inputType}
            required={field.required}
            placeholder={placeholder || fieldTypeLabel(field.type)}
            step={field.type === 'decimal' ? '0.01' : field.type === 'number' ? '1' : undefined}
            className={PREVIEW_INPUT}
          />
          {hint}
        </div>
      );
    }
  }
}

function PreviewPesoField({
  field,
  root,
  label,
  hint,
  inputId,
  placeholder,
}: {
  field: PreviewField;
  root: string;
  label: ReactNode;
  hint: ReactNode;
  inputId: string;
  placeholder: string;
}) {
  const [value, setValue] = useState('');

  return (
    <div className={root}>
      {label}
      <input
        id={inputId}
        name={inputId}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        required={field.required}
        placeholder={placeholder || '1.500.000'}
        className={PREVIEW_INPUT}
        value={value}
        onChange={(event) => {
          const input = event.currentTarget;
          const digitsBefore = digitsBeforeChileanPesoCaret(input.value, input.selectionStart ?? input.value.length);
          const next = formatChileanPeso(input.value);
          setValue(next);
          requestAnimationFrame(() => {
            const pos = chileanPesoCaret(next, digitsBefore);
            input.setSelectionRange(pos, pos);
          });
        }}
      />
      {hint}
    </div>
  );
}

function PreviewFileField({
  field,
  root,
  label,
  hint,
  inputId,
}: {
  field: PreviewField;
  root: string;
  label: ReactNode;
  hint: ReactNode;
  inputId: string;
}) {
  const maxFiles = Math.max(1, field.maxFiles ?? 1);
  const [names, setNames] = useState<string[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);

  useEffect(() => {
    return () => {
      for (const url of previews) URL.revokeObjectURL(url);
    };
  }, [previews]);

  function onChange(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []).slice(0, maxFiles);
    setNames(files.map((file) => file.name));
    setPreviews(field.type === 'image' ? files.map((file) => URL.createObjectURL(file)) : []);
  }

  return (
    <div className={root}>
      {label}
      {previews.length > 0 ? (
        <ul className="flex flex-wrap gap-3">
          {previews.map((src) => (
            <li key={src}>
              <img src={src} alt="" className="h-24 w-24 rounded-lg border border-dash-outline-variant object-cover" />
            </li>
          ))}
        </ul>
      ) : null}
      <label className="flex cursor-pointer flex-col items-center rounded-xl border-2 border-dashed border-dash-outline-variant bg-secondary-50 p-6 text-center transition-colors hover:border-dash-primary">
        <input
          id={inputId}
          name={inputId}
          type="file"
          accept={field.type === 'image' ? 'image/*' : undefined}
          multiple={maxFiles > 1}
          required={field.required && names.length === 0}
          className="sr-only"
          onChange={onChange}
        />
        <span className="material-symbols-outlined mb-2 text-3xl text-dash-primary" aria-hidden="true">
          {field.type === 'image' ? 'add_photo_alternate' : 'attach_file'}
        </span>
        <p className="text-sm font-medium text-dash-on-surface">
          {field.type === 'image'
            ? maxFiles > 1
              ? `Haga clic para subir imágenes (hasta ${maxFiles})`
              : 'Haga clic para subir una imagen'
            : maxFiles > 1
              ? `Haga clic para subir archivos (hasta ${maxFiles})`
              : 'Haga clic para subir un archivo'}
        </p>
        {maxFiles > 1 ? (
          <p className="mt-1 text-xs text-dash-on-surface-variant">
            {names.length} de {maxFiles} archivos
          </p>
        ) : names[0] ? (
          <p className="mt-1 text-xs text-dash-secondary">{names[0]}</p>
        ) : null}
      </label>
      {hint}
    </div>
  );
}
